// src/components/editor/EditorRuler.jsx
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const RULER_SIZE     = 24;    // px — thickness of each ruler bar
// Default (Letter portrait) — overridden by the pageWidth/pageHeight props so
// the ruler tracks whatever paper size + orientation is picked in Page Setup.
const DEFAULT_PAGE_WIDTH_PX  = 816;   // 8.5in × 96dpi
const DEFAULT_PAGE_HEIGHT_PX = 1056;  // 11in  × 96dpi
const PX_PER_INCH    = 96;

const MIN_MARGIN     = 12;
const MIN_PRINTABLE  = 72;  // px — smallest text strip a margin pair may leave

// Every .rv-page renders a fixed-height header and footer bar above/below the
// body. Their real rendered heights depend on box-sizing (this app loads MUI
// CssBaseline → border-box) and on whether the user has toggled them off, so
// EditorPage.jsx measures them from the live DOM and passes them in. These are
// only fallbacks used before the first measurement lands.
const DEFAULT_HEADER_HEIGHT = 41;
const DEFAULT_FOOTER_HEIGHT = 41;

// Layout constants — must match EditorPage.css exactly. Split by whether they
// live INSIDE .editable-content (which EditorPage.jsx visually scales by the
// current zoom via CSS transform) or OUTSIDE it (.editor-main-content's own
// padding, never transformed). Only the "inside" ones scale with zoom — see
// the scale-aware position math in HorizontalRuler/VerticalRuler below.
const CONTAINER_PAD_TOP = 24;  // editor-main-content padding-top — outside the transform, fixed screen px
const PAGE_MARGIN_TOP   = 12;  // .rv-page margin-top — inside the transform, scales with zoom
// Not the 24+12 that .rv-page's own `margin: 12px auto 24px` suggests:
// editor-inserts.css is imported after EditorPage.css, so at equal specificity
// its `.rv-page + .rv-page { margin-top: 48px }` wins for every page but the
// first. Down the block column those two margins collapse to max(24, 48), so
// the real gap is 48. This read 36 and quietly drifted the ruler ~12px per page
// against the pages it was ruling. The spread grid pins its own margins to
// 12/36 so it lands on the same total — see .is-spread in EditorPage.css.
const PAGE_GAP          = 48;  // collapsed gap between pages — inside the transform, scales as a whole
const BOTTOM_PADDING    = 56;  // editor-main-content padding-bottom — outside the transform, fixed screen px

// Fallback page count before EditorPage.jsx's live count lands (see pageCount
// prop on VerticalRuler) — was previously a hardcoded MAX_PAGES=20 ceiling that
// silently stopped ruling any document past page 20. Real docs can run much
// longer, so the tape now sizes itself to the actual page count.
const DEFAULT_PAGE_COUNT = 1;
// Browsers cap a single <canvas> at ~32,767px tall (Chrome/Firefox; Safari's
// limit is area-based and usually lower). Past this, ticks/shading simply stop
// being drawn — the draggable margin handles are plain DOM, not canvas, so
// they keep working at any page count; only the visual ruling on very long
// documents is truncated until a windowed (redraw-on-scroll) canvas replaces
// this "draw everything once" approach.
const MAX_CANVAS_HEIGHT = 32000;

// Vertical distance from one page's top to the next, in screen px. Exported so
// the status bar's page navigation measures pages exactly the way the ruler
// rules them — two copies of this arithmetic drifting apart is precisely how a
// page indicator ends up naming a different page than the one on screen.
// `gap` exists for the two-page spread, which closes the band between rows to
// zero — see .is-spread in EditorPage.css. The default is the single-page
// column's PAGE_GAP, so every existing caller is unchanged; a spread passes 0.
// It is a parameter rather than a second exported function because the whole
// reason this is exported is that two copies of the arithmetic drift apart,
// and two functions would drift exactly the same way.
export function pageStrideFor(pageHeight, scale = 1, gap = PAGE_GAP) {
  return (pageHeight + gap) * scale;
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
const formatInches = (px) => `${(px / PX_PER_INCH).toFixed(2)}"`;

// ─── Handle tooltip ───────────────────────────────────────────────────────────
// Named-label + live value shown the instant a handle is hovered or actively
// dragged — the ruler bar is only 24px tall/wide and clipped by its own
// overflow:hidden "window" (needed for the scroll-tape trick), so this can't
// be a CSS ::after on the handle itself; it'd be clipped away. Portaling to
// document.body and positioning from the handle's own getBoundingClientRect
// sidesteps that entirely, same trick the h-ruler alignment effect uses.
function HandleTooltip({ anchorEl, text, side }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!anchorEl) { setRect(null); return; }
    setRect(anchorEl.getBoundingClientRect());
  }, [anchorEl, text]);

  if (!anchorEl || !rect) return null;

  const positionStyle = side === 'right'
    ? { left: rect.right + 8, top: rect.top + rect.height / 2, transform: 'translate(0, -50%)' }
    : { left: rect.left + rect.width / 2, top: rect.top - 8, transform: 'translate(-50%, -100%)' };

  return createPortal(
    <div
      style={{
        position: 'fixed', ...positionStyle,
        background: '#1f2937', color: '#fff', fontSize: 11, fontFamily: 'Arial, sans-serif',
        padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
        pointerEvents: 'none', zIndex: 2000, boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
      }}
    >
      {text}
    </div>,
    document.body
  );
}

// Largest a margin can be while still leaving a MIN_PRINTABLE strip. Horizontal
// is bounded by the page width; vertical by the *body* height (page minus the
// header and footer chrome), since margins are measured inside the body.
function maxHMargin(pageWidth) { return Math.max(MIN_MARGIN, (pageWidth - MIN_PRINTABLE) / 2); }
function maxVMargin(pageHeight, headerH = DEFAULT_HEADER_HEIGHT, footerH = DEFAULT_FOOTER_HEIGHT) {
  return Math.max(MIN_MARGIN, (pageHeight - headerH - footerH - MIN_PRINTABLE) / 2);
}

// ─── Draw horizontal ruler canvas (redrawn whenever pageWidth or scale changes) ─
// pageWidth is LOGICAL (unscaled) — it decides how many inch ticks exist, which
// doesn't change with zoom. scale converts that count into actual screen px, so
// the ruler visually shrinks/grows in lockstep with the zoomed page (the ruler
// itself sits outside the page's CSS transform, so this has to be done by hand).
function drawHorizontalTicks(canvas, pageWidth, scale) {
  const dpr = window.devicePixelRatio || 1;
  const scaledWidth = pageWidth * scale;
  const pxPerInch = PX_PER_INCH * scale;

  canvas.width  = scaledWidth * dpr;
  canvas.height = RULER_SIZE * dpr;
  canvas.style.width  = `${scaledWidth}px`;
  canvas.style.height = `${RULER_SIZE}px`;

  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.fillStyle = '#5A52A3';
  ctx.fillRect(0, 0, scaledWidth, RULER_SIZE);
  ctx.strokeStyle = '#D4AF37';
  ctx.fillStyle   = '#D4AF37';
  ctx.font        = '9px Arial';
  ctx.textAlign   = 'center';

  const totalEighths = Math.round((pageWidth / PX_PER_INCH) * 8);
  for (let i = 0; i <= totalEighths; i++) {
    const px      = (i / 8) * pxPerInch;
    const tickLen = i % 8 === 0 ? 14 : i % 4 === 0 ? 9 : i % 2 === 0 ? 6 : 3;
    ctx.beginPath();
    ctx.moveTo(px, RULER_SIZE - tickLen);
    ctx.lineTo(px, RULER_SIZE);
    ctx.stroke();
    if (i % 8 === 0 && i > 0) ctx.fillText(String(i / 8), px, RULER_SIZE - tickLen - 2);
  }
}

// ─── Draw vertical tape canvas ───────────────────────────────────────────────
// Word/Docs model: the tape spans the whole page (so it reflects the real page
// size), the top/bottom margin zones — which contain the running header/footer
// — are shaded, and "0" sits at the boundary where text begins (top of the
// writable area, i.e. header + top-margin down from the page edge). Ticks then
// count down through the text area only; the shaded margins carry no numbers.
//
// All px/screen/* params here are already in SCREEN space (pre-multiplied by
// the caller using the current zoom, since firstPageOffset mixes a fixed
// container-padding term with a scaled page-margin term — see VerticalRuler).
// textHeightLogical is the one exception: it's UNSCALED, because it only
// decides how many inch ticks exist, which is zoom-independent.
// pageCount is the REAL number of pages (measured live in EditorPage.jsx) —
// long documents can exceed MAX_CANVAS_HEIGHT, in which case the canvas is
// clamped and ticks/shading simply stop past that point (draggable handles,
// being plain DOM rendered separately by VerticalRuler, are unaffected).
// Redrawn whenever page height, header/footer, margins, page count, or zoom change.
function drawTapeCanvas(canvas, tapeHeight, firstPageOffset, pageStride, pageHeight, headerH, footerH, marginTop, marginBottom, pxPerInch, textHeightLogical, pageCount) {
  const drawnHeight = Math.min(tapeHeight, MAX_CANVAS_HEIGHT);
  // Draw at 1× — tapeHeight can run into the tens of thousands of px for long
  // documents; multiplying by devicePixelRatio on Retina (2×) would double
  // that, blowing past every browser's max canvas height even sooner.
  canvas.width  = RULER_SIZE;
  canvas.height = drawnHeight;
  canvas.style.width  = `${RULER_SIZE}px`;
  canvas.style.height = `${drawnHeight}px`;

  const ctx = canvas.getContext('2d');

  // Base background
  ctx.fillStyle = '#5A52A3';
  ctx.fillRect(0, 0, RULER_SIZE, drawnHeight);

  // Page-break gap zones — subtle gold tint so users can see where pages split.
  // Early-exit once a page starts past the drawable canvas — cheap for the
  // common case, and avoids looping hundreds of times for long documents
  // whose tail is beyond MAX_CANVAS_HEIGHT anyway.
  const pageGapScreen = pageStride - pageHeight;
  for (let p = 0; p < pageCount; p++) {
    const gapTop = firstPageOffset + p * pageStride + pageHeight;
    if (gapTop > drawnHeight) break;
    ctx.fillStyle = 'rgba(212, 175, 55, 0.22)';
    ctx.fillRect(0, gapTop, RULER_SIZE, pageGapScreen);
  }

  // Margin zones — the non-writable band at the top (header + top margin) and
  // bottom (bottom margin + footer) of each page, shaded so the white text area
  // between them stands out. This is the "0 lines up with the first line" cue.
  for (let p = 0; p < pageCount; p++) {
    const pageTop   = firstPageOffset + p * pageStride;
    if (pageTop > drawnHeight) break;
    const textTop   = pageTop + headerH + marginTop;
    const textBot   = pageTop + pageHeight - footerH - marginBottom;
    ctx.fillStyle = 'rgba(212, 175, 55, 0.20)';  // matches the horizontal ruler's margin shade
    ctx.fillRect(0, pageTop, RULER_SIZE, textTop - pageTop);            // top margin zone
    ctx.fillRect(0, textBot, RULER_SIZE, pageTop + pageHeight - textBot); // bottom margin zone
  }

  // Tick marks and inch labels — measured DOWN THE TEXT AREA, so "0" sits
  // exactly where text begins (below the top margin) and lines up with the
  // first line, matching Word/Docs.
  ctx.strokeStyle = '#D4AF37';
  ctx.fillStyle   = '#D4AF37';
  ctx.font        = '9px Arial';
  ctx.textAlign   = 'center';

  const totalEighths = Math.max(0, Math.floor((textHeightLogical / PX_PER_INCH) * 8));

  for (let p = 0; p < pageCount; p++) {
    const textTop = firstPageOffset + p * pageStride + headerH + marginTop;
    if (textTop > drawnHeight) break;

    for (let i = 0; i <= totalEighths; i++) {
      const canvasPx = textTop + (i / 8) * pxPerInch;
      const tickLen  = i % 8 === 0 ? 14 : i % 4 === 0 ? 9 : i % 2 === 0 ? 6 : 3;

      ctx.beginPath();
      ctx.moveTo(RULER_SIZE - tickLen, canvasPx);
      ctx.lineTo(RULER_SIZE, canvasPx);
      ctx.stroke();

      // Label every inch (including 0 at the top of the text area)
      if (i % 8 === 0) {
        ctx.save();
        ctx.translate(RULER_SIZE / 2, canvasPx);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(String(i / 8), 0, 3);
        ctx.restore();
      }
    }
  }
}

// ─── Margin handle ────────────────────────────────────────────────────────────
// Horizontal margin handles are widened (18px vs the 10px visual arrow) so
// there's always a small exclusive strip on either side that no indent handle
// reaches — see IndentHandle below: when a paragraph has no custom indent,
// the indent markers sit at this exact same x, and without this margin would
// be fully unreachable there (indent's higher z-index would win every pixel).
function MarginHandle({ position, isVertical, onDragStart, side, label, value, dragging }) {
  const [hover, setHover] = useState(false);
  const elRef = useRef(null);
  const showTip = hover || dragging;

  const style = isVertical
    ? { position: 'absolute', top: position - 5, left: 0, width: RULER_SIZE, height: 10,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }
    : { position: 'absolute', left: position - 9, top: 0, width: 18, height: RULER_SIZE,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 };

  const scale = showTip ? 'scale(1.3)' : 'scale(1)';
  const arrowStyle = isVertical
    ? { width: 0, height: 0, transition: 'transform 0.1s ease', transform: scale,
        borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
        ...(side === 'start' ? { borderBottom: '8px solid #D4AF37' } : { borderTop: '8px solid #D4AF37' }) }
    : { width: 0, height: 0, transition: 'transform 0.1s ease', transform: scale,
        borderTop: '5px solid transparent', borderBottom: '5px solid transparent',
        ...(side === 'start' ? { borderRight: '8px solid #D4AF37' } : { borderLeft: '8px solid #D4AF37' }) };

  return (
    <div
      ref={elRef}
      style={style}
      onMouseDown={onDragStart}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={arrowStyle} />
      {showTip && (
        <HandleTooltip
          anchorEl={elRef.current}
          text={`${label}: ${formatInches(value)}`}
          side={isVertical ? 'right' : 'top'}
        />
      )}
    </div>
  );
}

// ─── Indent handle ────────────────────────────────────────────────────────────
// kind:
//   'first-line' — top triangle (▼), moves text-indent of selected paragraph
//   'left'       — bottom triangle (▲), moves margin-left of selected paragraph
//   'both'       — small square at very bottom, drags left+first-line together
//   'right'      — top triangle (▼) on right side, moves margin-right
const INDENT_COLOR = '#2563eb'; // blue — visually distinct from margin purple

const INDENT_LABELS = {
  'first-line': 'First-line Indent',
  left:         'Left Indent',
  both:         'Paragraph Indent',
  right:        'Right Indent',
};

function IndentHandle({ kind, position, onDragStart, value, dragging }) {
  const T = 7; // triangle half-width
  const [hover, setHover] = useState(false);
  const elRef = useRef(null);
  const showTip = hover || dragging;
  const hoverScale = showTip ? 1.3 : 1;

  const commonProps = {
    ref: elRef,
    onMouseDown: onDragStart,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };

  const tooltip = showTip && (
    <HandleTooltip
      anchorEl={elRef.current}
      text={`${INDENT_LABELS[kind]}: ${formatInches(value)}`}
      side="top"
    />
  );

  if (kind === 'first-line') {
    return (
      <div
        {...commonProps}
        style={{ position: 'absolute', left: position - T, top: 0,
                 width: T * 2, height: 10, cursor: 'pointer', zIndex: 4 }}
      >
        <div style={{ width: 0, height: 0, margin: '0 auto', transition: 'transform 0.1s ease',
                      transform: `scale(${hoverScale})`,
                      borderLeft: `${T}px solid transparent`,
                      borderRight: `${T}px solid transparent`,
                      borderTop: `${T}px solid ${INDENT_COLOR}` }} />
        {tooltip}
      </div>
    );
  }

  if (kind === 'left') {
    return (
      <div
        {...commonProps}
        style={{ position: 'absolute', left: position - T, bottom: 5,
                 width: T * 2, height: 10, cursor: 'pointer', zIndex: 4 }}
      >
        <div style={{ width: 0, height: 0, margin: '0 auto', transition: 'transform 0.1s ease',
                      transform: `scale(${hoverScale})`,
                      borderLeft: `${T}px solid transparent`,
                      borderRight: `${T}px solid transparent`,
                      borderBottom: `${T}px solid ${INDENT_COLOR}` }} />
        {tooltip}
      </div>
    );
  }

  if (kind === 'both') {
    // Square at very bottom — drag moves left + first-line together
    return (
      <div
        {...commonProps}
        style={{ position: 'absolute', left: position - T, bottom: 0,
                 width: T * 2, height: 5, cursor: 'pointer', transition: 'transform 0.1s ease',
                 transform: `scale(${hoverScale})`,
                 backgroundColor: INDENT_COLOR, zIndex: 5 }}
      >
        {tooltip}
      </div>
    );
  }

  if (kind === 'right') {
    return (
      <div
        {...commonProps}
        style={{ position: 'absolute', left: position - T, top: 0,
                 width: T * 2, height: 10, cursor: 'pointer', zIndex: 4 }}
      >
        <div style={{ width: 0, height: 0, margin: '0 auto', transition: 'transform 0.1s ease',
                      transform: `scale(${hoverScale})`,
                      borderLeft: `${T}px solid transparent`,
                      borderRight: `${T}px solid transparent`,
                      borderTop: `${T}px solid ${INDENT_COLOR}` }} />
        {tooltip}
      </div>
    );
  }

  return null;
}

// ─── Horizontal ruler ─────────────────────────────────────────────────────────
// pageWidth/margins/paraIndents are all LOGICAL (unscaled) px — the same units
// pageMargins/paraIndents state and .rv-page-body's real padding use. `scale`
// converts them to screen px for drawing/positioning, and drag deltas (already
// in screen px, from clientX) are divided by scale to get back to logical px
// before clamping/storing — the ruler sits outside the page's own CSS
// transform, so none of this scaling happens automatically.
// scrollLeft is consumed only as an effect dependency (see the alignment
// effect below) — the actual offset is measured, not computed from it.
function HorizontalRuler({ pageWidth, marginLeft, marginRight, onMarginsChange, paraIndents, onIndentChange, scale = 1, scrollLeft = 0 }) {
  const dragRef       = useRef(null);
  const canvasRef     = useRef(null);
  const containerRef  = useRef(null); // clipping window, sized to the viewport
  const tapeRef       = useRef(null); // full-width inner tape, translated to align
  const [windowWidth, setWindowWidth] = useState(pageWidth * scale);
  const maxHMarginRef = useRef(maxHMargin(pageWidth));
  maxHMarginRef.current = maxHMargin(pageWidth);
  // Which handle (if any) is actively being dragged — keeps that handle's
  // tooltip visible even when a fast drag carries the mouse off its small
  // hit-box (drag listeners live on window, so the drag itself keeps working;
  // only the hover-driven tooltip would otherwise disappear mid-drag).
  const [dragKind, setDragKind] = useState(null);

  // Redraw whenever the page width or zoom changes
  useEffect(() => {
    if (canvasRef.current) drawHorizontalTicks(canvasRef.current, pageWidth, scale);
  }, [pageWidth, scale]);

  // Window width — capped to the available viewport (mirrors the vertical
  // ruler's containerHeight measurement), so an over-wide/zoomed page clips
  // here instead of forcing this row wider than the scroll container below it.
  useEffect(() => {
    const scrollEl = document.querySelector('.editor-main-content');
    if (!scrollEl) return;
    const update = () => setWindowWidth(Math.max(0, scrollEl.clientWidth - RULER_SIZE));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, []);

  // Keep the tape's horizontal offset pixel-perfect with the real rendered
  // page position. This ruler lives OUTSIDE .editor-main-content (a separate
  // Box above it), so it can't just mirror scrollLeft the way the vertical
  // ruler mirrors scrollTop — .editor-main-content centers its content with
  // justify-content:center when it fits and scrolls it when it doesn't, and
  // independently re-deriving that centering/overflow math here would be
  // fragile to keep in sync. Measuring the real page's getBoundingClientRect
  // sidesteps that entirely: whatever the container did, this reads the truth.
  // scrollLeft is only a dependency to trigger recompute on scroll — its
  // numeric value isn't used directly.
  useEffect(() => {
    const align = () => {
      const page = document.querySelector('.editor-main-content .rv-page');
      const windowEl = containerRef.current;
      if (!page || !windowEl || !tapeRef.current) return;
      const offsetX = page.getBoundingClientRect().left - windowEl.getBoundingClientRect().left;
      tapeRef.current.style.transform = `translateX(${offsetX}px)`;
    };
    align();
    // One more pass next frame in case this run raced a layout that hadn't
    // settled yet (e.g. right after a zoom or paper-size change).
    const raf = requestAnimationFrame(align);
    return () => cancelAnimationFrame(raf);
  }, [scrollLeft, windowWidth, pageWidth, scale]);

  // ── Margin drag ──
  const startMarginDrag = useCallback((side) => (e) => {
    e.preventDefault();
    dragRef.current = { kind: 'margin', side, startX: e.clientX,
                        startMargin: side === 'left' ? marginLeft : marginRight };
    setDragKind(side === 'left' ? 'margin-left' : 'margin-right');
    const onMove = (mv) => {
      const { side: s, startX, startMargin } = dragRef.current;
      const dxLogical = (s === 'left' ? mv.clientX - startX : startX - mv.clientX) / scale;
      const raw = startMargin + dxLogical;
      const max = maxHMarginRef.current;
      onMarginsChange(s === 'left'
        ? { left:  clamp(raw, MIN_MARGIN, max) }
        : { right: clamp(raw, MIN_MARGIN, max) });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragKind(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [marginLeft, marginRight, onMarginsChange, scale]);

  // ── Indent drag ──
  // printable width = the area between the two margin handles
  // leftIndentPos  = marginLeft + paraIndents.left          (abs ruler pos of ▲)
  // firstLinePos   = leftIndentPos + paraIndents.firstLine  (abs ruler pos of ▼)
  // rightIndentPos = pageWidth - marginRight - paraIndents.right
  const startIndentDrag = useCallback((kind) => (e) => {
    if (!onIndentChange) return;
    e.preventDefault();
    dragRef.current = {
      kind,
      startX:     e.clientX,
      startLeft:  paraIndents.left,
      startFirst: paraIndents.firstLine,
      startRight: paraIndents.right,
    };
    setDragKind(`indent-${kind}`);
    const printable = pageWidth - marginLeft - marginRight;
    const onMove = (mv) => {
      const dx = (mv.clientX - dragRef.current.startX) / scale;
      const { startLeft, startFirst, startRight } = dragRef.current;

      if (kind === 'firstLine') {
        // Move only first-line; left indent stays, text-indent adjusts
        const newFirst = clamp(startFirst + dx, -startLeft, printable - startLeft - 1);
        onIndentChange({ firstLine: newFirst });

      } else if (kind === 'left') {
        // Move left indent; first-line absolute pos STAYS → text-indent changes
        const firstLineAbs = startLeft + startFirst; // absolute offset from marginLeft
        const newLeft = clamp(startLeft + dx, 0, printable - 1);
        const newFirst = clamp(firstLineAbs - newLeft, -(newLeft), printable - newLeft - 1);
        onIndentChange({ left: newLeft, firstLine: newFirst });

      } else if (kind === 'both') {
        // Move both together; preserve firstLine relative offset
        const newLeft = clamp(startLeft + dx, 0, printable - 1);
        onIndentChange({ left: newLeft });

      } else if (kind === 'right') {
        // Drag left = more right indent (ruler pos moves left)
        const newRight = clamp(startRight - dx, 0, printable - 1);
        onIndentChange({ right: newRight });
      }
    };
    const onUp = () => {
      dragRef.current = null;
      setDragKind(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pageWidth, marginLeft, marginRight, paraIndents, onIndentChange, scale]);

  // Absolute ruler positions for indent handles — logical px (matches printable
  // above), scaled to screen px only at render time, below.
  const leftIndentPos  = marginLeft + (paraIndents?.left      ?? 0);
  const firstLinePos   = leftIndentPos + (paraIndents?.firstLine ?? 0);
  const rightIndentPos = pageWidth - marginRight - (paraIndents?.right ?? 0);

  return (
    <div ref={containerRef} className="rv-ruler rv-ruler-h" style={{ width: windowWidth }}>
      {/* Tape is full (zoomed) page width; translateX (set imperatively above)
          keeps it aligned with the real page regardless of centering/scroll. */}
      <div ref={tapeRef} className="rv-ruler-tape-h" style={{ width: pageWidth * scale }}>
        <canvas ref={canvasRef} />

        {/* Margin shading + handles — positions scaled to screen px for display */}
        <div className="rv-ruler-margin-shade" style={{ left: 0, width: marginLeft * scale }} />
        <div className="rv-ruler-margin-shade" style={{ right: 0, width: marginRight * scale }} />
        <MarginHandle position={marginLeft * scale}                isVertical={false} side="start"
                      label="Left Margin"  value={marginLeft}  dragging={dragKind === 'margin-left'}
                      onDragStart={startMarginDrag('left')}  />
        <MarginHandle position={(pageWidth - marginRight) * scale} isVertical={false} side="end"
                      label="Right Margin" value={marginRight} dragging={dragKind === 'margin-right'}
                      onDragStart={startMarginDrag('right')} />

        {/* Indent handles — only when a paragraph is focused and onIndentChange provided */}
        {onIndentChange && (
          <>
            {/* First-line indent — top triangle ▼ */}
            <IndentHandle kind="first-line" position={firstLinePos * scale} value={paraIndents?.firstLine ?? 0}
                          dragging={dragKind === 'indent-firstLine'} onDragStart={startIndentDrag('firstLine')} />
            {/* Left indent — bottom triangle ▲ */}
            <IndentHandle kind="left"       position={leftIndentPos * scale}  value={paraIndents?.left ?? 0}
                          dragging={dragKind === 'indent-left'} onDragStart={startIndentDrag('left')} />
            {/* Combined square — drag both together */}
            <IndentHandle kind="both"       position={leftIndentPos * scale}  value={paraIndents?.left ?? 0}
                          dragging={dragKind === 'indent-both'} onDragStart={startIndentDrag('both')} />
            {/* Right indent — top triangle ▼ on right */}
            <IndentHandle kind="right"      position={rightIndentPos * scale} value={paraIndents?.right ?? 0}
                          dragging={dragKind === 'indent-right'} onDragStart={startIndentDrag('right')} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Vertical ruler (tape approach) ──────────────────────────────────────────
// The outer container is sticky + overflow:hidden — a fixed "window".
// Inside lives a tall tape that translates by -scrollTop via a DOM mutation
// (no React re-render on scroll). pageCount is the REAL, live page count
// (measured in EditorPage.jsx) — the tape/scroll area always sizes to the true
// document length; only the canvas's own drawing is internally capped past
// MAX_CANVAS_HEIGHT (see drawTapeCanvas) since a single <canvas> can't grow
// unbounded. Margin handles and shading live inside the tape and auto-translate.
// pageHeight/headerHeight/footerHeight/margins are all LOGICAL (unscaled) px.
// `scale` is the current zoom. Two DIFFERENT things happen to screen position
// here, and they must not be conflated:
//   - CONTAINER_PAD_TOP / BOTTOM_PADDING are .editor-main-content's own padding
//     — outside the page's CSS scale transform — so they stay fixed screen px.
//   - Everything else (PAGE_MARGIN_TOP, PAGE_GAP, pageHeight, header/footer,
//     margins, tick spacing) lives INSIDE .editable-content, so it scales.
// scrollTop is already real screen px (native browser scroll offset against
// the already-scaled sizer from Phase 1), so it needs no extra conversion.
function VerticalRuler({ pageHeight, headerHeight, footerHeight, marginTop, marginBottom, onMarginsChange, scrollTop, scale = 1, pageCount = DEFAULT_PAGE_COUNT }) {
  const containerRef = useRef(null);
  const tapeRef      = useRef(null);
  const canvasRef    = useRef(null);
  const dragRef      = useRef(null);
  const [containerHeight, setContainerHeight] = useState(600);
  const [dragSide, setDragSide] = useState(null); // 'top' | 'bottom' | null — for tooltip persistence mid-drag

  const pages = Math.max(1, pageCount);

  // Screen-space (scaled) geometry — see the doc comment above.
  const pageHeightS    = pageHeight * scale;
  const headerHeightS  = headerHeight * scale;
  const footerHeightS  = footerHeight * scale;
  const marginTopS     = marginTop * scale;
  const marginBottomS  = marginBottom * scale;
  const pxPerInch      = PX_PER_INCH * scale;
  const firstPageOffset = CONTAINER_PAD_TOP + PAGE_MARGIN_TOP * scale;
  const pageStride      = pageStrideFor(pageHeight, scale);
  // The scrollable tape always reflects the TRUE document length — never
  // capped — so scrolling/pagination stay correct regardless of how long the
  // document is. Only the canvas's own drawing is internally clamped.
  const tapeHeight       = firstPageOffset + pages * pageStride + BOTTOM_PADDING;
  // Logical text height only decides tick COUNT, which is zoom-independent.
  const textHeightLogical = pageHeight - headerHeight - footerHeight - marginTop - marginBottom;

  // Keep the live max in a ref so the drag handler (created once per margin
  // change) always clamps against the current page geometry. Logical units,
  // since pageMargins state (what this ultimately sets) is always logical.
  const maxVMarginRef = useRef(0);
  maxVMarginRef.current = maxVMargin(pageHeight, headerHeight, footerHeight);

  // Redraw whenever the geometry the scale depends on changes: page height,
  // header/footer heights (paper-size / orientation switch, header-footer
  // toggle), the margins (which move the "0" text-start boundary), page
  // count, or zoom. Redraws are cheap (~24px-wide canvas) so doing it live
  // during a margin drag or a zoom change is fine.
  useEffect(() => {
    if (canvasRef.current) {
      drawTapeCanvas(
        canvasRef.current, tapeHeight, firstPageOffset, pageStride,
        pageHeightS, headerHeightS, footerHeightS, marginTopS, marginBottomS,
        pxPerInch, textHeightLogical, pages
      );
    }
  }, [tapeHeight, firstPageOffset, pageStride, pageHeightS, headerHeightS, footerHeightS,
      marginTopS, marginBottomS, pxPerInch, textHeightLogical, pages]);

  // Observe the scroll container height so the ruler window matches it
  useEffect(() => {
    const scrollEl = containerRef.current?.closest('.editor-main-content');
    if (!scrollEl) return;
    const update = () => setContainerHeight(scrollEl.clientHeight || 600);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, []);

  // Sync tape translation with scroll — DOM mutation only, zero React re-renders
  useEffect(() => {
    if (tapeRef.current) {
      tapeRef.current.style.transform = `translateY(${-scrollTop}px)`;
    }
  }, [scrollTop]);

  const startDrag = useCallback((side) => (e) => {
    e.preventDefault();
    dragRef.current = { side, startY: e.clientY, startMargin: side === 'top' ? marginTop : marginBottom };
    setDragSide(side);
    const onMove = (mv) => {
      const { side: s, startY, startMargin } = dragRef.current;
      const dyLogical = (s === 'top' ? mv.clientY - startY : startY - mv.clientY) / scale;
      const raw = startMargin + dyLogical;
      const max = maxVMarginRef.current;
      onMarginsChange(s === 'top'
        ? { top:    clamp(raw, MIN_MARGIN, max) }
        : { bottom: clamp(raw, MIN_MARGIN, max) });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragSide(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [marginTop, marginBottom, onMarginsChange, scale]);

  return (
    <div ref={containerRef} className="rv-ruler rv-ruler-v" style={{ height: containerHeight }}>
      {/*
        The tape is as tall as tapeHeight (pages × pageStride, the real page count).
        It translates by -scrollTop in perfect sync with the document.
        overflow:hidden on the container clips what's off-screen.
        Handles and shading live inside the tape so they auto-translate.
      */}
      <div ref={tapeRef} className="rv-ruler-tape">
        {/* The canvas paints the scale AND the shaded margin zones, so it stays
            in sync with the ticks in one pass (up to MAX_CANVAS_HEIGHT — see
            drawTapeCanvas). Handles are plain DOM and unaffected by that cap. */}
        <canvas ref={canvasRef} />

        {Array.from({ length: pages }, (_, p) => {
          const pageTop   = firstPageOffset + p * pageStride;
          // "0" is the top of the text area: page edge + header + top margin.
          // The bottom handle sits at the bottom-margin / footer boundary.
          // All screen-space (scaled) — matches what drawTapeCanvas painted.
          const topPos    = pageTop + headerHeightS + marginTopS;
          const bottomPos = pageTop + pageHeightS - footerHeightS - marginBottomS;

          return (
            <React.Fragment key={p}>
              {/* Top margin handle */}
              <MarginHandle position={topPos}    isVertical side="start"
                            label="Top Margin"    value={marginTop}    dragging={dragSide === 'top'}
                            onDragStart={startDrag('top')}    />
              {/* Bottom margin handle */}
              <MarginHandle position={bottomPos} isVertical side="end"
                            label="Bottom Margin" value={marginBottom} dragging={dragSide === 'bottom'}
                            onDragStart={startDrag('bottom')} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Named exports for split placement ───────────────────────────────────────
// EditorHRuler: corner + h-ruler bar — place OUTSIDE the scroll container
// `scale` is the current zoom (EditorPage.jsx's `zoom` state) — the ruler sits
// outside the page's own CSS transform, so it must be told the scale explicitly
// to stay visually in sync with the zoomed page. Defaults to 1 (no zoom).
export function EditorHRuler({ pageWidth = DEFAULT_PAGE_WIDTH_PX, scale = 1, scrollLeft = 0, margins, onMarginsChange, paraIndents, onIndentChange }) {
  const handleChange = useCallback(
    (partial) => onMarginsChange({ ...margins, ...partial }),
    [margins, onMarginsChange]
  );
  return (
    <>
      <div className="rv-ruler-corner" />
      <HorizontalRuler
        pageWidth={pageWidth}
        scale={scale}
        scrollLeft={scrollLeft}
        marginLeft={margins.left}
        marginRight={margins.right}
        onMarginsChange={handleChange}
        paraIndents={paraIndents}
        onIndentChange={onIndentChange}
      />
    </>
  );
}

// EditorVRuler: vertical tape ruler — place INSIDE the scroll container
export function EditorVRuler({
  pageHeight = DEFAULT_PAGE_HEIGHT_PX,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  footerHeight = DEFAULT_FOOTER_HEIGHT,
  scale = 1,
  pageCount = DEFAULT_PAGE_COUNT,
  margins, onMarginsChange, scrollTop = 0,
}) {
  const handleChange = useCallback(
    (partial) => onMarginsChange({ ...margins, ...partial }),
    [margins, onMarginsChange]
  );
  return (
    <VerticalRuler
      pageHeight={pageHeight}
      headerHeight={headerHeight}
      footerHeight={footerHeight}
      scale={scale}
      pageCount={pageCount}
      marginTop={margins.top}
      marginBottom={margins.bottom}
      onMarginsChange={handleChange}
      scrollTop={scrollTop}
    />
  );
}

// ─── Legacy default export (kept for backward compat) ────────────────────────
export default function EditorRuler({
  pageWidth, pageHeight, headerHeight, footerHeight, scale, scrollLeft = 0, pageCount,
  margins, onMarginsChange, scrollTop = 0,
}) {
  return (
    <>
      <EditorHRuler pageWidth={pageWidth} scale={scale} scrollLeft={scrollLeft} margins={margins} onMarginsChange={onMarginsChange} />
      <EditorVRuler
        pageHeight={pageHeight}
        headerHeight={headerHeight}
        footerHeight={footerHeight}
        scale={scale}
        pageCount={pageCount}
        margins={margins}
        onMarginsChange={onMarginsChange}
        scrollTop={scrollTop}
      />
    </>
  );
}

// Exported so EditorPage.jsx can clamp pageMargins to the current page size
// whenever paper size / orientation changes in Page Setup.
export { maxHMargin, maxVMargin };
